// ─────────────────────────────────────────────────────────────────────
// Tenon · TaskDetailPeek — field pickers (dropdowns for each field).
//
// One file per concern, one dropdown design per field. Every picker:
//   • Renders a PickerTrigger (dot + label + chevron) when canEdit
//   • Renders a PickerStatic (dot + label, no chevron, no border) otherwise
//   • Opens a Popover with field-specific UI when clicked
//
// Coverage:
//   KindPicker, TaskTypePicker, PriorityPicker, StatusPicker,
//   AssigneePicker (multi), ProjectPicker, PhasePicker,
//   ParentTaskPicker, EquipmentPicker (combobox),
//   RepeatPicker, GoalPicker, BillablePicker,
//   DateTimePicker (start), DatePicker (due), DurationInput
//
// Static-text fallbacks: PickerStatic, TextStatic, NotesStatic
// ─────────────────────────────────────────────────────────────────────

import React from 'react'
import { Icon, Avatar, Popover } from '@/design-system/primitives'
import { formatDateWithOptions, formatTimeWithOptions } from '../../utils/dateUtils'
import { taskGrants } from '@/lib/api'
import { useLiveAssignablePeople, groupBySection, personTypeLabel } from './assignee-picker-shared'
import { PersonStatusPill } from '@/components/atoms/PersonStatusPill'
import { portalStatusFromAssignablePerson } from '@/lib/portalStatus'

import { authedFetch } from '@/lib/api';
// ─── Exported data types ─────────────────────────────────────────────

export interface PhaseOption {
  id: string
  name: string
  parentId?: string | null
  color?: string
  range?: string
  /** Owning project — when set, PhasePicker filters out phases from other projects (PEEK-SCOPING-01). */
  projectId?: string | number | null
}

export interface LocationOption {
  id: string
  name: string
  parentId?: string | null
  projectId?: string
  isProject?: boolean
  hint?: string
}

export interface AssigneeOption {
  id: string
  name: string
  initials?: string
  color?: string
  role?: string
  /** Only populated in PeekAssigneePicker's live (projectId-scoped) data source — see portalStatusFromAssignablePerson (#1548). */
  portalStatus?: string
  /** Contact's own company name (subs/vendors) — live-source only, see AssignablePerson.company. */
  company?: string
  /** Contact's trade (subs) — live-source only, see AssignablePerson.trade. */
  trade?: string
}

export interface EquipmentOption {
  id: string
  name: string
  serial?: string
  hint?: string
}

export interface GoalOption {
  id: string
  name: string
}

export interface TaskKind {
  id: string
  label: string
  dot: string
}

// ─── Internal option catalogues ──────────────────────────────────────

const PRIORITIES: { id: string; label: string; dot: string }[] = [
  { id: 'urgent', label: 'Urgent', dot: 'var(--priority-urgent-dot)' },
  { id: 'high',   label: 'High',   dot: 'var(--priority-high-dot)' },
  { id: 'normal', label: 'Normal', dot: 'var(--priority-normal-dot)' },
  { id: 'low',    label: 'Low',    dot: 'var(--priority-low-dot)' },
]

const STATUSES: { id: string; label: string }[] = [
  { id: 'not_started', label: 'Not started' },
  { id: 'in_progress', label: 'In progress' },
  { id: 'blocked',     label: 'Blocked' },
  { id: 'completed',   label: 'Completed' },
  { id: 'on_hold',     label: 'On hold' },
  { id: 'cancelled',   label: 'Cancelled' },
]

const REPEATS: { id: string; label: string; hint?: string }[] = [
  { id: 'none',    label: 'No repeat' },
  { id: 'daily',   label: 'Daily',           hint: 'Mon–Fri' },
  { id: 'weekly',  label: 'Weekly',          hint: 'Every Tue' },
  { id: 'biweek',  label: 'Every other week' },
  { id: 'monthly', label: 'Monthly',         hint: 'Day 18' },
  { id: 'custom',  label: 'Custom…' },
]

const BILLABLE: { id: string; label: string; hint?: string }[] = [
  { id: 'billable',     label: 'Billable',     hint: 'On client invoice' },
  { id: 'non_billable', label: 'Non-billable', hint: 'Internal' },
  { id: 'tm',           label: 'T&M',          hint: 'Time & materials' },
]

const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S']
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

// ─── Shared atoms ────────────────────────────────────────────────────

interface PickerTriggerProps {
  dot?: string | null
  label?: string | null
  placeholder?: boolean
  onClick?: React.MouseEventHandler<HTMLButtonElement>
  trailing?: React.ReactNode
  style?: React.CSSProperties
  'aria-expanded'?: boolean
  'aria-haspopup'?: boolean
  /** Linear/Height-style inline property row (task-peek-web.jsx `Prop`/`ValTrig`):
   *  no border, no background box, 30px row, chevron fades in on hover.
   *  Used inside CompactRow (Who/Where/When/Details). Default keeps the
   *  original bordered 40px cell used by the title-row pills. */
  compact?: boolean
}

function PickerTrigger({
  dot, label, placeholder, onClick, trailing, style = {}, compact = false,
  'aria-expanded': ariaExpanded,
  'aria-haspopup': ariaHaspopup,
}: PickerTriggerProps) {
  const [hover, setHover] = React.useState(false)
  const boxedStyle: React.CSSProperties = {
    width: '100%', textAlign: 'left',
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '10px 12px', minHeight: 40,
    background: 'var(--bg-elevated)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--r-sm)', cursor: 'pointer',
    fontFamily: 'var(--font-sans)', fontSize: 13.5, fontWeight: 600,
    color: placeholder ? 'var(--text-dim)' : 'var(--text-primary)',
    transition: 'border-color var(--dur-fast) var(--ease-out)',
  }
  const compactStyle: React.CSSProperties = {
    width: '100%', textAlign: 'left',
    display: 'flex', alignItems: 'center', gap: 7,
    padding: '0 6px', minHeight: 30,
    background: 'transparent',
    border: 'none',
    borderRadius: 6, cursor: 'pointer',
    fontFamily: 'var(--font-sans)', fontSize: 13, fontWeight: 500,
    color: placeholder ? 'var(--text-dim)' : 'var(--text-primary)',
  }
  return (
    <button
      type="button"
      onClick={onClick}
      aria-expanded={ariaExpanded}
      aria-haspopup={ariaHaspopup}
      style={{ ...(compact ? compactStyle : boxedStyle), ...style }}
      onMouseEnter={(e) => { compact ? setHover(true) : (e.currentTarget.style.borderColor = 'var(--border-strong)') }}
      onMouseLeave={(e) => { compact ? setHover(false) : (e.currentTarget.style.borderColor = 'var(--border)') }}
    >
      {dot && (
        <span style={{
          width: 8, height: 8, borderRadius: '50%',
          background: dot, flexShrink: 0,
        }}/>
      )}
      <span style={{
        flex: 1, overflow: 'hidden', textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}>{label}</span>
      {trailing ?? (
        <Icon name="chevD" size={13} sw={2}
          style={{
            color: 'var(--text-dim)', flexShrink: 0,
            opacity: compact ? (hover ? 1 : 0) : 1,
            transition: 'opacity .12s ease',
          }}/>
      )}
    </button>
  )
}

interface PickerStaticProps {
  dot?: string | null
  label?: string | null
  placeholder?: boolean
  leading?: React.ReactNode
  style?: React.CSSProperties
  compact?: boolean
}

function PickerStatic({ dot, label, placeholder, leading = null, style = {}, compact = false }: PickerStaticProps) {
  return (
    <div style={{
      width: '100%',
      display: 'flex', alignItems: 'center', gap: compact ? 7 : 8,
      padding: compact ? '0 6px' : '8px 0', minHeight: compact ? 30 : 32,
      fontFamily: 'var(--font-sans)', fontSize: compact ? 13 : 13.5, fontWeight: 500,
      color: placeholder ? 'var(--text-dim)' : 'var(--text-primary)',
      ...style,
    }}>
      {leading}
      {dot && (
        <span style={{
          width: 8, height: 8, borderRadius: '50%',
          background: dot, flexShrink: 0,
        }}/>
      )}
      <span style={{
        flex: 1, overflow: 'hidden', textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}>{placeholder ? '—' : label}</span>
    </div>
  )
}

interface TextStaticProps {
  value?: string | null
  placeholder?: string
  mono?: boolean
}

export function TextStatic({ value, placeholder = '—', mono = false }: TextStaticProps) {
  const empty = value == null || value === ''
  return (
    <div style={{
      padding: '8px 0', minHeight: 32,
      fontFamily: mono ? 'var(--font-mono)' : 'var(--font-sans)',
      fontSize: 13.5, fontWeight: 500,
      color: empty ? 'var(--text-dim)' : 'var(--text-primary)',
    }}>{empty ? placeholder : value}</div>
  )
}

interface NotesStaticProps {
  value?: string | null
}

export function NotesStatic({ value }: NotesStaticProps) {
  const empty = !value
  return (
    <div style={{
      padding: empty ? '8px 0' : '12px 14px',
      background: empty ? 'transparent' : 'var(--bg-elevated)',
      border: empty ? 'none' : '1px solid var(--border)',
      borderRadius: 'var(--r-sm)',
      fontFamily: 'var(--font-sans)', fontSize: 13, lineHeight: 1.55,
      color: empty ? 'var(--text-dim)' : 'var(--text-primary)',
      whiteSpace: 'pre-wrap',
    }}>{empty ? 'No notes.' : value}</div>
  )
}

interface OptionListProps {
  children: React.ReactNode
  maxHeight?: number
  search?: React.ReactNode
  footer?: React.ReactNode
}

function OptionList({ children, maxHeight = 280, search = null, footer = null }: OptionListProps) {
  return (
    <div style={{ width: 260 }}>
      {search}
      <div style={{
        maxHeight, overflowY: 'auto',
        padding: 4,
      }}>{children}</div>
      {footer}
    </div>
  )
}

interface OptionRowProps {
  dot?: string | null
  leading?: React.ReactNode
  label: string
  hint?: string | null
  checked?: boolean
  onClick?: (e?: React.MouseEvent<HTMLButtonElement>) => void
  danger?: boolean
}

function OptionRow({ dot = null, leading = null, label, hint = null, checked = false, onClick, danger = false }: OptionRowProps) {
  return (
    <button
      onClick={onClick}
      style={{
        width: '100%', textAlign: 'left', cursor: 'pointer',
        background: 'transparent', border: 'none',
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '8px 10px', borderRadius: 'var(--r-sm)',
        fontFamily: 'var(--font-sans)', fontSize: 13,
        color: danger ? 'var(--st-error)' : 'var(--text-primary)',
        transition: 'background var(--dur-fast) var(--ease-out)',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--row-hover)' }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent' }}
    >
      {dot && (
        <span style={{
          width: 8, height: 8, borderRadius: '50%',
          background: dot, flexShrink: 0,
        }}/>
      )}
      {leading}
      <span style={{
        flex: 1, fontWeight: 500,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>{label}</span>
      {hint && (
        <span style={{
          fontFamily: 'var(--font-sans)', fontSize: 11.5, fontWeight: 500,
          color: 'var(--text-muted)',
        }}>{hint}</span>
      )}
      {checked && (
        <Icon name="check" size={13} sw={2}
          style={{ color: 'var(--brand-gold)', flexShrink: 0 }}/>
      )}
    </button>
  )
}

interface PopoverSearchProps {
  value: string
  onChange: (v: string) => void
  placeholder?: string
}

function PopoverSearch({ value, onChange, placeholder = 'Search…' }: PopoverSearchProps) {
  return (
    <div style={{
      padding: 8, borderBottom: '1px solid var(--border)',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6,
        padding: '6px 10px',
        background: 'var(--bg-elevated)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--r-sm)',
      }}>
        <Icon name="search" size={12} sw={2}
          style={{ color: 'var(--text-muted)', flexShrink: 0 }}/>
        <input
          autoFocus
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          style={{
            flex: 1, background: 'transparent', border: 'none', outline: 'none',
            fontFamily: 'var(--font-sans)', fontSize: 12.5,
            color: 'var(--text-primary)',
          }}
        />
      </div>
    </div>
  )
}

interface PopoverFooterProps {
  children: React.ReactNode
  style?: React.CSSProperties
}

function PopoverFooter({ children, style = {} }: PopoverFooterProps) {
  return (
    <div style={{
      padding: 8, borderTop: '1px solid var(--border)',
      display: 'flex', alignItems: 'center', gap: 8,
      ...style,
    }}>{children}</div>
  )
}

interface FooterActionProps {
  icon?: string
  label: string
  onClick?: React.MouseEventHandler<HTMLButtonElement>
  tone?: 'brand' | 'muted'
}

function FooterAction({ icon = 'plus', label, onClick, tone = 'brand' }: FooterActionProps) {
  const color = tone === 'brand' ? 'var(--brand-gold)' : 'var(--text-muted)'
  return (
    <button
      onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        background: 'transparent', border: 'none', cursor: 'pointer',
        color, fontFamily: 'var(--font-sans)', fontSize: 12.5, fontWeight: 600,
        padding: '4px 6px', borderRadius: 'var(--r-sm)',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--row-hover)' }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent' }}
    >
      <Icon name={icon} size={12} sw={2}/>
      {label}
    </button>
  )
}

// ─── Phase helper ─────────────────────────────────────────────────────

function phasePath(id: string, tree: PhaseOption[]): PhaseOption[] {
  const path: PhaseOption[] = []
  let cur: PhaseOption | undefined = tree.find(p => p.id === id)
  while (cur) {
    path.unshift(cur)
    const parentId = cur.parentId
    cur = parentId ? tree.find(p => p.id === parentId) : undefined
  }
  return path
}

// ─── Calendar internals ───────────────────────────────────────────────

function buildCalendar(year: number, month: number): (number | null)[] {
  const first = new Date(year, month, 1)
  const startDay = first.getDay()
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const cells: (number | null)[] = []
  for (let i = 0; i < startDay; i++) cells.push(null)
  for (let d = 1; d <= daysInMonth; d++) cells.push(d)
  while (cells.length % 7 !== 0) cells.push(null)
  return cells
}

interface CalendarBodyProps {
  year: number
  month: number
  selectedISO?: string | null
  onSelect?: (day: number) => void
}

function CalendarBody({ year, month, selectedISO, onSelect }: CalendarBodyProps) {
  const cells = buildCalendar(year, month)
  const today = new Date()
  const todayY = today.getFullYear(), todayM = today.getMonth(), todayD = today.getDate()
  const sel = selectedISO ? new Date(selectedISO + 'T00:00:00') : null

  return (
    <div style={{ padding: '4px 10px 10px' }}>
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2,
        fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 700,
        letterSpacing: 0.6, color: 'var(--text-muted)',
        textAlign: 'center', padding: '4px 0',
      }}>
        {WEEKDAYS.map((d, i) => <div key={i}>{d}</div>)}
      </div>
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2,
      }}>
        {cells.map((c, i) => {
          if (c == null) return <div key={i}/>
          const isToday = c === todayD && month === todayM && year === todayY
          const isSel = sel != null && c === sel.getDate() && month === sel.getMonth() && year === sel.getFullYear()
          return (
            <button
              key={i}
              onMouseDown={() => onSelect?.(c)}
              style={{
                aspectRatio: '1 / 1', minHeight: 30,
                background: isSel ? 'var(--brand-gold)' : 'transparent',
                color: isSel
                  ? 'var(--brand-gold-ink)'
                  : isToday ? 'var(--brand-gold)' : 'var(--text-primary)',
                border: isToday && !isSel
                  ? '1px solid var(--brand-gold)'
                  : '1px solid transparent',
                borderRadius: 'var(--r-sm)', cursor: 'pointer',
                fontFamily: 'var(--font-sans)', fontSize: 12.5, fontWeight: isSel ? 700 : 500,
                transition: 'background var(--dur-fast) var(--ease-out)',
              }}
              onMouseEnter={(e) => {
                if (!isSel) e.currentTarget.style.background = 'var(--row-hover)'
              }}
              onMouseLeave={(e) => {
                if (!isSel) e.currentTarget.style.background = 'transparent'
              }}
            >{c}</button>
          )
        })}
      </div>
    </div>
  )
}

interface CalendarHeaderProps {
  year: number
  month: number
  onPrev: () => void
  onNext: () => void
}

function CalendarHeader({ year, month, onPrev, onNext }: CalendarHeaderProps) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      padding: '8px 12px',
    }}>
      <button
        onClick={onPrev}
        aria-label="Previous month"
        style={{
          width: 24, height: 24, padding: 0, borderRadius: 'var(--r-sm)',
          background: 'transparent', border: 'none', cursor: 'pointer',
          color: 'var(--text-muted)',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <Icon name="chevL" size={14} sw={2}/>
      </button>
      <div style={{
        flex: 1, textAlign: 'center',
        fontFamily: 'var(--font-sans)', fontSize: 13, fontWeight: 700,
        color: 'var(--text-primary)',
      }}>{MONTH_NAMES[month]} {year}</div>
      <button
        onClick={onNext}
        aria-label="Next month"
        style={{
          width: 24, height: 24, padding: 0, borderRadius: 'var(--r-sm)',
          background: 'transparent', border: 'none', cursor: 'pointer',
          color: 'var(--text-muted)',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <Icon name="chevR" size={14} sw={2}/>
      </button>
    </div>
  )
}

function parseDateValue(v: string | null | undefined): Date | null {
  if (!v) return null
  // Pure date (no time component) — treat as local midnight.
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) {
    const d = new Date(v + 'T00:00:00')
    return isNaN(d.getTime()) ? null : d
  }
  // Full datetime (ISO, with or without a "T" time / offset) — parse as-is
  // so DateTimePicker round-trips the stored time instead of losing it.
  if (/^\d{4}-\d{2}-\d{2}T/.test(v)) {
    const d = new Date(v)
    return isNaN(d.getTime()) ? null : d
  }
  const d = new Date(v + ' 2026')
  return isNaN(d.getTime()) ? null : d
}

function fmtDate(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${m}-${day}`
}

function fmtDisplay(d: Date, withTime: boolean): string {
  const out = formatDateWithOptions(d, { month: 'short', day: 'numeric', year: 'numeric' })
  if (!withTime) return out
  const t = formatTimeWithOptions(d, { hour: 'numeric', minute: '2-digit' })
  return `${out}, ${t}`
}

// ─── Priority + Status pickers ───────────────────────────────────────

export interface PriorityPickerProps {
  value?: string
  onChange?: (v: string) => void
  canEdit: boolean
}

export function PriorityPicker({ value = 'normal', onChange, canEdit }: PriorityPickerProps) {
  const cur = PRIORITIES.find(p => p.id === value) ?? PRIORITIES[2]
  if (!canEdit) return <PickerStatic dot={cur.dot} label={cur.label}/>
  return (
    <Popover
      side="bottom" align="start" width={200}
      trigger={<PickerTrigger dot={cur.dot} label={cur.label}/>}
      closeOnSelect
    >
      <OptionList>
        {PRIORITIES.map(p => (
          <OptionRow key={p.id} dot={p.dot} label={p.label}
            checked={p.id === value} onClick={() => onChange?.(p.id)}/>
        ))}
      </OptionList>
    </Popover>
  )
}

export interface StatusPickerProps {
  value?: string
  onChange?: (v: string) => void
  canEdit: boolean
}

export function StatusPicker({ value = 'not_started', onChange, canEdit }: StatusPickerProps) {
  const cur = STATUSES.find(s => s.id === value) ?? STATUSES[0]
  const dot = (id: string) => `var(--status-${id.replace(/_/g, '-')}-dot)`
  if (!canEdit) return <PickerStatic dot={dot(cur.id)} label={cur.label}/>
  return (
    <Popover
      side="bottom" align="start" width={220}
      trigger={<PickerTrigger dot={dot(cur.id)} label={cur.label}/>}
      closeOnSelect
    >
      <OptionList>
        {STATUSES.map(s => (
          <OptionRow key={s.id} dot={dot(s.id)} label={s.label}
            checked={s.id === value} onClick={() => onChange?.(s.id)}/>
        ))}
      </OptionList>
    </Popover>
  )
}

// ─── KindPicker + TaskTypePicker ──────────────────────────────────────

export interface KindPickerProps {
  kind?: string
  value?: string
  onChange?: (v: string) => void
  canEdit: boolean
  list?: TaskKind[]
}

export function KindPicker({ kind = 'schedule', value, onChange, canEdit, list = [] }: KindPickerProps) {
  const cur = list.find(k => k.id === value) ?? list[0]
  if (!cur) return null
  if (!canEdit) return <PickerStatic dot={cur.dot} label={cur.label}/>
  return (
    <Popover
      side="bottom" align="start" width={220}
      trigger={<PickerTrigger dot={cur.dot} label={cur.label}/>}
      closeOnSelect
    >
      <OptionList>
        {list.map(k => (
          <OptionRow key={k.id} dot={k.dot} label={k.label}
            checked={k.id === value} onClick={() => onChange?.(k.id)}/>
        ))}
      </OptionList>
    </Popover>
  )
}

export interface TaskTypeOption {
  id: string
  label: string
  dot: string
}

export interface TaskTypePickerProps {
  value?: string
  onChange?: (v: string) => void
  canEdit: boolean
  options?: TaskTypeOption[]
  compact?: boolean
}

export function TaskTypePicker({ value, onChange, canEdit, options = [], compact = false }: TaskTypePickerProps) {
  const cur = options.find(t => t.id === value) ?? options[0]
  if (!cur) return null
  if (!canEdit) return <PickerStatic dot={cur.dot} label={cur.label} compact={compact}/>
  return (
    <Popover
      side="bottom" align="start" width={220}
      trigger={<PickerTrigger dot={cur.dot} label={cur.label} compact={compact}/>}
      closeOnSelect
    >
      <OptionList footer={
        <PopoverFooter>
          <FooterAction label="Manage task types" icon="settings" tone="muted"/>
        </PopoverFooter>
      }>
        {options.map(t => (
          <OptionRow key={t.id} dot={t.dot} label={t.label}
            checked={t.id === cur.id} onClick={() => onChange?.(t.id)}/>
        ))}
      </OptionList>
    </Popover>
  )
}

// ─── AssigneePicker — multi-select with search ───────────────────────

interface AssigneeChipProps {
  person: AssigneeOption
  onRemove?: () => void
  canEdit: boolean
}

function AssigneeChip({ person, onRemove, canEdit }: AssigneeChipProps) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '4px 4px 4px 6px',
      background: 'var(--bg-elevated)',
      border: '1px solid var(--border)',
      borderRadius: 999,
      fontFamily: 'var(--font-sans)', fontSize: 12, fontWeight: 600,
      color: 'var(--text-primary)',
    }}>
      <Avatar id={person.initials ?? person.id} size={18}/>
      {person.name.split(' ')[0]}
      <PersonStatusPill status={person.portalStatus} />
      {canEdit && onRemove && (
        <button
          onClick={(e) => { e.stopPropagation(); e.preventDefault(); onRemove() }}
          aria-label={`Remove ${person.name}`}
          style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 16, height: 16, minHeight: 0, minWidth: 0, padding: 0,
            background: 'transparent', border: 'none', cursor: 'pointer',
            color: 'var(--text-muted)', borderRadius: '50%',
          }}
        >
          <Icon name="x" size={10} sw={2.4}/>
        </button>
      )}
    </span>
  )
}

// Row shape for the compact PeekAssigneePicker popover list — name primary,
// role · trade · company secondary (SCHED-W4 §12: "every result has a name…
// identical row shape whether or not the person is on this project").
function PeekAssigneeRow({ p, checked, noAccess, onClick }: {
  p: AssigneeOption
  checked: boolean
  noAccess: boolean
  onClick: (e?: React.MouseEvent) => void
}) {
  const secondary = [p.role, p.trade, p.company].filter(Boolean).join(' · ')
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        width: '100%', textAlign: 'left', cursor: 'pointer',
        background: 'transparent', border: 'none',
        display: 'flex', alignItems: 'center', gap: 9,
        padding: '7px 10px', borderRadius: 'var(--r-sm)',
        transition: 'background var(--dur-fast) var(--ease-out)',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--row-hover)' }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent' }}
    >
      <Avatar id={p.initials ?? p.id} size={24}/>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{
          display: 'block', fontFamily: 'var(--font-sans)', fontSize: 13, fontWeight: 600,
          color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{p.name}</span>
        {secondary && (
          <span style={{
            display: 'block', fontFamily: 'var(--font-sans)', fontSize: 11.5, color: 'var(--text-muted)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>{secondary}</span>
        )}
      </span>
      {noAccess && (
        <span style={{
          display: 'inline-flex', alignItems: 'center', flexShrink: 0,
          padding: '1px 6px', borderRadius: 999,
          background: 'color-mix(in srgb, var(--st-warning) 14%, transparent)',
          border: '1px solid color-mix(in srgb, var(--st-warning) 35%, transparent)',
          color: 'var(--st-warning)',
          fontFamily: 'var(--font-sans)', fontSize: 10, fontWeight: 700,
        }} title="Not on this project — assigning will prompt a grant">+ Access</span>
      )}
      {checked && (
        <Icon name="check" size={13} sw={2.2} style={{ color: 'var(--brand-gold)', flexShrink: 0 }}/>
      )}
    </button>
  )
}

export interface PeekAssigneePickerProps {
  value?: string[]
  currentNames?: string[] | null
  onChange?: (v: string[]) => void
  canEdit: boolean
  options?: AssigneeOption[]
  /**
   * When set, the picker owns its data source instead of client-filtering the
   * static `options` prop: the unsearched view is the project's roster and
   * typing runs a debounced, real backend search across the whole company
   * directory (access-scoped-search contract, #803). `options` is used as a
   * fallback only while the roster fetch for the current projectId is in
   * flight or fails.
   */
  projectId?: string | null
  compact?: boolean
  /**
   * The task being edited, when it already exists (CU-P4). Selecting a
   * not-on-project contact calls POST /tasks/{taskId}/grant-access (#804)
   * instead of silently adding them — same grant-access-confirm flow as the
   * molecule AssigneePicker, previously missing here. Omit for a task still
   * being drafted; the confirm still shows, but the add is local-only (the
   * server applies the same rule once the draft is submitted).
   */
  taskId?: string
  /** Display name for the confirm copy ("X isn't on {projectName}"). */
  projectName?: string | null
}

export function PeekAssigneePicker({ value = [], currentNames, onChange, canEdit, options = [], projectId, compact = false, taskId, projectName }: PeekAssigneePickerProps) {
  const [query, setQuery] = React.useState('')

  // Canonical live data source in both states. A project scopes the default
  // roster and annotates access; without one, the same endpoint returns the
  // company directory with employees first. Identity never falls back to the
  // raw users-array role labels merely because projectId is absent.
  const { people: liveAssignable, accessIds } = useLiveAssignablePeople(projectId ?? undefined, query)
  const livePeople = React.useMemo(
    () => liveAssignable?.map(p => ({ id: String(p.id), name: p.name, role: personTypeLabel(p.person_type), company: p.company || undefined, trade: p.trade || undefined, portalStatus: portalStatusFromAssignablePerson(p) })) ?? null,
    [liveAssignable],
  )
  // Map from option id -> the raw AssignablePerson, so the grant-access check
  // can tell a no-login contact (contact_id set) from a login user.
  const assignableById = React.useMemo(
    () => new Map((liveAssignable ?? []).map(p => [String(p.id), p] as const)),
    [liveAssignable],
  )

  const effectiveOptions = livePeople ?? options
  // Once the live directory has loaded, the server already applied roster or
  // company-search scoping, so no client-side query filter is re-applied.
  const filtered = livePeople != null
    ? effectiveOptions
    : effectiveOptions.filter(t =>
        !query ||
        t.name.toLowerCase().includes(query.toLowerCase()) ||
        (t.role ?? '').toLowerCase().includes(query.toLowerCase())
      )

  // Grant-access confirm (#804) — mirrors molecules/AssigneePicker's flow. A
  // not-on-project contact is staged here instead of being added straight
  // away. Only meaningful in live mode (accessIds only ever comes back from
  // the projectId-scoped fetch).
  const accessSet = React.useMemo(() => new Set(accessIds || []), [accessIds])
  const [pendingGrant, setPendingGrant] = React.useState<AssigneeOption | null>(null)
  const [granting, setGranting] = React.useState(false)
  const [grantError, setGrantError] = React.useState<string | null>(null)

  const isNoAccess = (opt: AssigneeOption): boolean => {
    if (accessIds == null) return false
    const person = assignableById.get(opt.id)
    return !!person && !person.is_user && person.contact_id != null && !accessSet.has(person.contact_id)
  }

  // Invite by email (SCHED-W4 §12: "stays as a footer action, always
  // available" — #2430 AC2). Mirrors molecules/AssigneePicker's working
  // invite flow onto the same task-scoped collaborators endpoint. Only
  // offered once there's a project (and an existing task) to invite into —
  // same precondition handleInviteByEmail enforces there.
  const canInviteByEmail = !!projectId && !!taskId
  const [invitingByEmail, setInvitingByEmail] = React.useState(false)
  const [inviteEmail, setInviteEmail] = React.useState('')
  const [inviting, setInviting] = React.useState(false)
  const [inviteError, setInviteError] = React.useState<string | null>(null)
  const closeInvite = () => { setInvitingByEmail(false); setInviteEmail(''); setInviteError(null) }
  const submitInvite = async () => {
    if (!inviteEmail.trim() || !projectId) return
    setInviting(true)
    setInviteError(null)
    try {
      const res = await authedFetch(`/api/v1/projects/${projectId}/collaborators`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: inviteEmail.trim(), task_id: taskId }),
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}) as Record<string, unknown>)
        throw new Error((err as { error?: string }).error || 'Failed to invite')
      }
      closeInvite()
    } catch (e) {
      setInviteError(e instanceof Error ? e.message : 'Could not invite')
    } finally {
      setInviting(false)
    }
  }
  const inviteByEmailForm = (
    <div style={{ padding: '10px 10px 4px', display: 'flex', flexDirection: 'column', gap: 8 }}>
      <input
        type="email" autoFocus placeholder="Email address"
        value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)}
        disabled={inviting}
        style={{
          padding: '8px 10px', borderRadius: 'var(--r-sm)', border: '1px solid var(--border)',
          fontSize: 13, fontFamily: 'inherit', width: '100%', boxSizing: 'border-box',
          background: 'var(--bg-input, var(--bg-elevated))', color: 'var(--text-primary)',
        }}
      />
      {inviteError && <div style={{ fontSize: 11.5, color: 'var(--st-error)' }}>{inviteError}</div>}
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" onClick={submitInvite} disabled={inviting || !inviteEmail.trim()} style={{
          flex: 1.4, minHeight: 30, borderRadius: 6, border: 'none',
          background: 'var(--brand-gold)', color: 'var(--brand-gold-ink, #1a1a1a)', fontFamily: 'inherit',
          fontSize: 12.5, fontWeight: 700, cursor: inviting ? 'default' : 'pointer', opacity: inviting ? 0.7 : 1,
        }}>{inviting ? 'Inviting…' : 'Send invite'}</button>
        <button type="button" onClick={closeInvite} disabled={inviting} style={{
          flex: 1, minHeight: 30, borderRadius: 6, border: '1px solid var(--border-strong)',
          background: 'transparent', color: 'var(--text-primary)', fontFamily: 'inherit',
          fontSize: 12.5, fontWeight: 600, cursor: inviting ? 'default' : 'pointer',
        }}>Cancel</button>
      </div>
    </div>
  )

  const addValue = (id: string) => { if (!value.includes(id)) onChange?.([...value, id]) }

  const requestAdd = (opt: AssigneeOption) => {
    if (isNoAccess(opt)) { setGrantError(null); setPendingGrant(opt); return }
    addValue(opt.id)
  }

  const confirmGrant = async () => {
    const opt = pendingGrant
    const person = opt ? assignableById.get(opt.id) : null
    if (!opt || !person?.contact_id) return
    if (!taskId) { addValue(opt.id); setPendingGrant(null); return }
    setGranting(true)
    setGrantError(null)
    try {
      await taskGrants.grant(taskId, person.contact_id)
      addValue(opt.id)
      setPendingGrant(null)
    } catch (e) {
      setGrantError(e instanceof Error ? e.message : 'Could not grant access')
    } finally {
      setGranting(false)
    }
  }

  const grantConfirmBanner = pendingGrant && (
    <div style={{
      display: 'flex', flexDirection: 'column', gap: 8, margin: '4px 10px 8px',
      padding: '10px 12px', borderRadius: 8,
      border: '1px solid color-mix(in oklch, var(--st-warning, var(--brand-gold)) 30%, transparent)',
      background: 'color-mix(in oklch, var(--st-warning, var(--brand-gold)) 8%, transparent)',
      fontFamily: 'var(--font-sans)',
    }}>
      <div>
        <div style={{ fontSize: 13, fontWeight: 700 }}>
          {pendingGrant.name} isn&rsquo;t on {projectName || 'this project'}
        </div>
        <div style={{ marginTop: 4, fontSize: 12, lineHeight: 1.5, color: 'var(--text-secondary)' }}>
          Assigning adds them to the project team with <strong>Crew</strong> access — they&rsquo;ll see this task, its location, and the schedule.
        </div>
        {grantError && <div style={{ marginTop: 4, fontSize: 12, color: 'var(--st-error)' }}>{grantError}</div>}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" onClick={() => setPendingGrant(null)} disabled={granting} style={{
          flex: 1, minHeight: 30, borderRadius: 6, border: '1px solid var(--border-strong)',
          background: 'transparent', color: 'var(--text-primary)', fontFamily: 'inherit',
          fontSize: 12.5, fontWeight: 600, cursor: granting ? 'default' : 'pointer',
        }}>Cancel</button>
        <button type="button" onClick={confirmGrant} disabled={granting} style={{
          flex: 1.4, minHeight: 30, borderRadius: 6, border: 'none',
          background: 'var(--brand-gold)', color: 'var(--brand-gold-ink, #1a1a1a)', fontFamily: 'inherit',
          fontSize: 12.5, fontWeight: 700, cursor: granting ? 'default' : 'pointer', opacity: granting ? 0.7 : 1,
        }}>{granting ? 'Granting…' : 'Assign + grant access'}</button>
      </div>
    </div>
  )

  // Every id in `value` renders as a pill, independent of the currently
  // -visible options list. Previously `selected` was filtered straight from
  // `effectiveOptions` — in live mode that list is REPLACED by each debounced
  // search's narrow result set, so a committed assignee who didn't match the
  // in-flight search text silently vanished from the pill row the moment a
  // 2nd person was searched for and picked (looked like "2nd replaces 1st",
  // #970). Resolve each id from whichever source has it, falling back to the
  // positional name the task was hydrated with (`currentNames`, index-aligned
  // with `value` — see task-detail-peek.tsx's `assigneeNames` construction).
  const optionsById = new Map(effectiveOptions.map(o => [o.id, o] as const))
  const selected = value
    .map((id, i) => optionsById.get(id) ?? (currentNames?.[i] ? { id, name: currentNames[i] } : null))
    .filter((p): p is AssigneeOption => p != null)

  const chips = (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
      {selected.map(p => (
        <AssigneeChip key={p.id} person={p} canEdit={canEdit}
          onRemove={() => onChange?.(value.filter(v => v !== p.id))}/>
      ))}
      {selected.length === 0 && (
        <span style={{
          fontFamily: 'var(--font-sans)', fontSize: 13, fontStyle: 'italic',
          color: 'var(--text-dim)',
        }}>Unassigned</span>
      )}
    </div>
  )

  if (!canEdit) {
    return <div style={{ padding: compact ? '0 6px' : '6px 0' }}>{chips}</div>
  }

  // Compact mode (Linear/Height property row): chips + a trailing "+" trigger
  // inline in one row, matching task-peek-web.jsx's single-row Assignees Prop —
  // not the stacked chips-then-boxed-trigger layout used elsewhere.
  if (compact) {
    return (
      <Popover
        side="bottom" align="start" width={280}
        trigger={
          <button type="button" style={{
            all: 'unset', width: '100%', cursor: 'pointer', boxSizing: 'border-box',
            display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6,
            minHeight: 30, padding: '0 6px', borderRadius: 6,
          }}>
            {selected.length > 0 ? (
              <>
                {selected.map(p => (
                  <AssigneeChip key={p.id} person={p} canEdit
                    onRemove={() => onChange?.(value.filter(v => v !== p.id))}/>
                ))}
                <span style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: 20, height: 20, borderRadius: '50%',
                  border: '1px dashed var(--border)', color: 'var(--text-dim)',
                  flexShrink: 0,
                }} aria-label="Add assignee" title="Add assignee">
                  <Icon name="plus" size={11}/>
                </span>
              </>
            ) : (
              <span style={{ fontFamily: 'var(--font-sans)', fontSize: 13, color: 'var(--text-dim)' }}>Assign crew</span>
            )}
          </button>
        }
      >
        <OptionList
          search={<PopoverSearch value={query} onChange={setQuery} placeholder="Search people…"/>}
          footer={
            <PopoverFooter>
              {canInviteByEmail && (
                <FooterAction icon="msg" label="Invite by email" onClick={() => setInvitingByEmail(true)}/>
              )}
              <div style={{ flex: 1 }}/>
              {selected.length > 0 && (
                <FooterAction icon="x" label="Clear" tone="muted" onClick={() => onChange?.([])}/>
              )}
            </PopoverFooter>
          }
        >
          {invitingByEmail ? (
            inviteByEmailForm
          ) : pendingGrant ? (
            // Grant is a step, not a warning (SCHED-W4 §12): picking an
            // off-project person swaps the whole list for the confirm strip
            // instead of banner-above-list, so the choice is unambiguous.
            grantConfirmBanner
          ) : (
            <>
              {filtered.length === 0 && (
                <div style={{
                  padding: '14px 10px', fontFamily: 'var(--font-sans)',
                  fontSize: 12.5, color: 'var(--text-muted)',
                  fontStyle: 'italic', textAlign: 'center',
                }}>No teammates match.</div>
              )}
              {(() => {
                const renderRow = (p: AssigneeOption) => {
                  const checked = value.includes(p.id)
                  const noAccess = !checked && isNoAccess(p)
                  return (
                    <PeekAssigneeRow key={p.id} p={p} checked={checked} noAccess={noAccess}
                      onClick={(e) => {
                        e?.stopPropagation?.()
                        if (checked) onChange?.(value.filter(v => v !== p.id))
                        else requestAdd(p)
                      }}/>
                  )
                }
                // Two labelled groups — project team, then anyone else in the
                // org (SCHED-W4 §12) — only when live project-scoped data
                // tells us who's on-project (`accessIds`). Falls back to a
                // flat list for callers with no projectId (e.g. bulk-assign
                // bar), same as before this change.
                if (accessIds == null) return filtered.map(renderRow)
                const onProj = filtered.filter(p => {
                  const person = assignableById.get(p.id)
                  return !!person?.contact_id && accessSet.has(person.contact_id)
                })
                const offProj = filtered.filter(p => !onProj.includes(p))
                const header = (t: string) => (
                  <div key={`h:${t}`} style={{
                    padding: '6px 10px 3px', fontFamily: 'var(--font-sans)',
                    fontSize: 10.5, fontWeight: 700, letterSpacing: 0.6,
                    textTransform: 'uppercase', color: 'var(--text-muted)',
                  }}>{t}</div>
                )
                return (
                  <>
                    {onProj.length > 0 && header(`On ${projectName || 'this project'}`)}
                    {onProj.map(renderRow)}
                    {offProj.length > 0 && header('Elsewhere in your org')}
                    {offProj.map(renderRow)}
                  </>
                )
              })()}
            </>
          )}
        </OptionList>
      </Popover>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {/* Removable assignee pills — each has an X (rendered OUTSIDE the trigger
          button so the X isn't an invalid button-in-button and its click
          removes the person rather than opening the add-popover). */}
      {selected.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
          {selected.map(p => (
            <AssigneeChip key={p.id} person={p} canEdit
              onRemove={() => onChange?.(value.filter(v => v !== p.id))}/>
          ))}
        </div>
      )}
    <Popover
      side="bottom" align="start" width={280}
      trigger={
        <button type="button" style={{
          width: '100%', textAlign: 'left',
          display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6,
          padding: '8px 12px', minHeight: 40,
          background: 'var(--bg-elevated)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--r-sm)', cursor: 'pointer',
          fontFamily: 'var(--font-sans)', fontSize: 13, fontWeight: 600,
        }}>
          <span style={{ color: 'var(--text-dim)' }}>
            {selected.length === 0 && !(currentNames && currentNames.length) ? '+ Add assignee…' : '+ Add assignee'}
          </span>
        </button>
      }
    >
      <OptionList
        search={<PopoverSearch value={query} onChange={setQuery} placeholder="Search people…"/>}
        footer={
          <PopoverFooter>
            {canInviteByEmail && (
              <FooterAction icon="msg" label="Invite by email" onClick={() => setInvitingByEmail(true)}/>
            )}
            <div style={{ flex: 1 }}/>
            {selected.length > 0 && (
              <FooterAction icon="x" label="Clear" tone="muted"
                onClick={() => onChange?.([])}/>
            )}
          </PopoverFooter>
        }
      >
        {invitingByEmail ? inviteByEmailForm : (
          <>
            {grantConfirmBanner}
            {filtered.length === 0 && (
              <div style={{
                padding: '14px 10px', fontFamily: 'var(--font-sans)',
                fontSize: 12.5, color: 'var(--text-muted)',
                fontStyle: 'italic', textAlign: 'center',
              }}>No teammates match.</div>
            )}
            {(() => {
              // Canonical section order/grouping (shared with molecules/AssigneePicker
              // — see assignee-picker-shared.ts). `role` carries the contact person_type.
              const renderRow = (p: AssigneeOption) => {
                const checked = value.includes(p.id)
                const noAccess = !checked && isNoAccess(p)
                return (
                  <OptionRow key={p.id}
                    leading={<Avatar id={p.initials ?? p.id} size={22}/>}
                    label={p.name} hint={noAccess ? 'No project access — will prompt a grant' : p.role} checked={checked}
                    onClick={(e) => {
                      e?.stopPropagation?.()
                      if (checked) onChange?.(value.filter(v => v !== p.id))
                      else requestAdd(p)
                    }}/>
                )
              }
              return groupBySection(filtered).map(b => (
                <React.Fragment key={b.key}>
                  <div style={{
                    padding: '6px 10px 3px', fontFamily: 'var(--font-sans)',
                    fontSize: 10.5, fontWeight: 700, letterSpacing: 0.6,
                    textTransform: 'uppercase', color: 'var(--text-muted)',
                  }}>{b.label}</div>
                  {b.items.map(renderRow)}
                </React.Fragment>
              ))
            })()}
          </>
        )}
      </OptionList>
    </Popover>
    </div>
  )
}

// ─── ProjectPicker ────────────────────────────────────────────────────

export interface ProjectPickerProps {
  value?: string
  currentName?: string | null
  onChange?: (v: string | null) => void
  canEdit: boolean
  options?: { id: string; name: string; color?: string; status?: string }[]
  compact?: boolean
}

export function ProjectPicker({ value, currentName, onChange, canEdit, options = [], compact = false }: ProjectPickerProps) {
  const [query, setQuery] = React.useState('')
  // Match by id only. currentName is the canonical display label from the task's
  // own joined data — never re-resolve from options to avoid silent fallback bugs.
  const cur = options.find(p => p.id === value)
  const displayName = cur?.name ?? currentName

  const swatch = (color?: string) => (
    <span style={{
      width: 14, height: 14, borderRadius: 4,
      background: color ?? 'var(--text-muted)', flexShrink: 0,
    }}/>
  )

  if (!canEdit) {
    if (!displayName) return <PickerStatic placeholder label="No project" compact={compact}/>
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: compact ? '0 6px' : '8px 0', minHeight: compact ? 30 : 32,
        fontFamily: 'var(--font-sans)', fontSize: 13.5, fontWeight: 500,
      }}>
        {swatch(cur?.color)} {displayName}
      </div>
    )
  }

  return (
    <Popover
      side="bottom" align="start" width={280}
      trigger={
        <PickerTrigger
          label={displayName ?? 'Select project…'}
          placeholder={!displayName}
          compact={compact}
          style={compact ? undefined : { paddingLeft: 10 }}
        />
      }
      closeOnSelect
    >
      <OptionList
        search={<PopoverSearch value={query} onChange={setQuery} placeholder="Search projects…"/>}
      >
        <OptionRow
          label="No project"
          checked={!value}
          onClick={() => onChange?.(null)}
        />
        {options
          .filter(p => !query || p.name.toLowerCase().includes(query.toLowerCase()))
          .map(p => (
            <OptionRow key={p.id}
              leading={swatch(p.color)}
              label={p.name} hint={p.status}
              checked={p.id === value}
              onClick={() => onChange?.(p.id)}/>
          ))}
      </OptionList>
    </Popover>
  )
}

// ─── PhasePicker ──────────────────────────────────────────────────────

export interface PhasePickerProps {
  value?: string | null
  currentName?: string | null
  onChange?: (v: string | null) => void
  canEdit: boolean
  phases?: PhaseOption[]
  label?: string
  onCreatePhase?: (parentId?: string) => void
  projectId?: string | null
  compact?: boolean
}

export function PhasePicker({ value = null, currentName, onChange, canEdit, phases = [], label = 'Phase', onCreatePhase, projectId, compact = false }: PhasePickerProps) {
  const [extraPhases, setExtraPhases] = React.useState<PhaseOption[]>([])
  // De-dupe by id, keeping the first occurrence — `phases` (the refreshed panel
  // cache, #619) comes first so it wins over any stale inline-created entry.
  // Then scope to the current project (PEEK-SCOPING-01): keep untagged phases
  // (caller didn't attach a projectId) plus phases matching `projectId`, same
  // permissive rule as AssigneePicker/ParentTaskPicker.
  const allPhases = React.useMemo(() => {
    const seen = new Set<string>()
    const deduped = [...phases, ...extraPhases].filter(p => {
      const k = String(p.id)
      if (seen.has(k)) return false
      seen.add(k); return true
    })
    if (!projectId) return deduped
    return deduped.filter(p => p.projectId == null || String(p.projectId) === String(projectId))
  }, [phases, extraPhases, projectId])
  const cur = allPhases.find(p => p.id === value)
  const [query, setQuery] = React.useState('')
  const [inlineCreate, setInlineCreate] = React.useState<false | { parentId?: string }>(false)
  const [createName, setCreateName] = React.useState('')
  const [creating, setCreating] = React.useState(false)

  const handleInlineCreate = async () => {
    const name = createName.trim()
    if (!name || creating) return
    setCreating(true)
    try {
      const body: Record<string, unknown> = { name }
      if (projectId) body.project_public_id = projectId
      if (inlineCreate && inlineCreate.parentId) body.phase_id = inlineCreate.parentId
      const res = await authedFetch(`/api/v1/phases`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }, { redirectOnAuthFailure: false })
      if (res.ok) {
        const created = await res.json()
        const newPhase: PhaseOption = {
          id: created.id,
          name: created.name || name,
          parentId: (inlineCreate && inlineCreate.parentId) || null,
        }
        setExtraPhases(prev => [...prev, newPhase])
        onChange?.(newPhase.id)
      }
    } catch {/* silent */}
    setCreating(false)
    setInlineCreate(false)
    setCreateName('')
  }

  const swatch = (color?: string) => (
    <span style={{ width: 12, height: 12, borderRadius: 3, background: color ?? 'var(--text-muted)', flexShrink: 0 }}/>
  )

  if (!canEdit) {
    if (!cur && !currentName) return <PickerStatic placeholder label={`No ${label.toLowerCase()}`} compact={compact}/>
    if (!cur && currentName) {
      return (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: compact ? '0 6px' : '8px 0', minHeight: compact ? 30 : 32,
          fontFamily: 'var(--font-sans)', fontSize: 13.5, fontWeight: 500,
        }}>
          {swatch()} <span>{currentName}</span>
        </div>
      )
    }
    const path = phasePath(cur!.id, allPhases)
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6,
        padding: compact ? '0 6px' : '8px 0', minHeight: compact ? 30 : 32,
        fontFamily: 'var(--font-sans)', fontSize: 13.5, fontWeight: 500,
      }}>
        {swatch(cur!.color)}
        {path.map((p, i) => (
          <React.Fragment key={p.id}>
            {i > 0 && (
              <Icon name="chevR" size={9} sw={2} style={{ color: 'var(--text-dim)' }}/>
            )}
            <span style={{
              color: i === path.length - 1 ? 'var(--text-primary)' : 'var(--text-muted)',
              fontWeight: i === path.length - 1 ? 600 : 500,
            }}>{p.name}</span>
          </React.Fragment>
        ))}
        {cur!.range && (
          <span style={{ color: 'var(--text-muted)', fontSize: 11.5, marginLeft: 4 }}>
            · {cur!.range}
          </span>
        )}
      </div>
    )
  }

  // Flatten tree in DFS order with depth
  const flat: (PhaseOption & { _depth: number })[] = []
  const visit = (parentId: string | null | undefined, depth: number) => {
    for (const p of allPhases.filter(x => (x.parentId ?? null) === (parentId ?? null))) {
      flat.push({ ...p, _depth: depth })
      visit(p.id, depth + 1)
    }
  }
  visit(null, 0)

  const q = query.trim().toLowerCase()
  const filtered = q ? flat.filter(p => p.name.toLowerCase().includes(q)) : flat

  const newPhaseLabel = cur ? `New sub-phase under ${cur.name}` : 'New phase'
  const newPhaseParentId = cur ? cur.id : undefined

  const inlineCreateFooter = inlineCreate !== false ? (
    <div style={{ padding: 8, borderTop: '1px solid var(--border)' }}>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <input
          autoFocus
          value={createName}
          onChange={e => setCreateName(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') { e.preventDefault(); handleInlineCreate() }
            if (e.key === 'Escape') { setInlineCreate(false); setCreateName('') }
          }}
          placeholder="Phase name…"
          style={{
            flex: 1, padding: '6px 8px',
            fontFamily: 'var(--font-sans)', fontSize: 12.5,
            color: 'var(--text-primary)',
            background: 'var(--bg-elevated)',
            border: '1px solid var(--border-strong)',
            borderRadius: 'var(--r-sm)',
            outline: 'none',
          }}
        />
        <button type="button" onClick={handleInlineCreate} disabled={creating || !createName.trim()} style={{
          padding: '5px 10px', borderRadius: 'var(--r-sm)',
          background: 'var(--brand-gold)', border: 'none',
          fontFamily: 'var(--font-sans)', fontSize: 12, fontWeight: 600,
          color: '#000', cursor: 'pointer', opacity: !createName.trim() ? 0.5 : 1,
        }}>
          {creating ? '…' : 'Create'}
        </button>
        <button type="button" onClick={() => { setInlineCreate(false); setCreateName('') }} style={{
          padding: '5px 8px', borderRadius: 'var(--r-sm)',
          background: 'transparent', border: '1px solid var(--border)',
          fontFamily: 'var(--font-sans)', fontSize: 12, cursor: 'pointer',
          color: 'var(--text-muted)',
        }}>✕</button>
      </div>
    </div>
  ) : null

  return (
    <Popover
      side="bottom" align="start" width={320}
      trigger={
        <PickerTrigger
          dot={cur ? cur.color : undefined}
          label={cur?.name ?? currentName ?? `No ${label.toLowerCase()}`}
          placeholder={!cur && !currentName}
          compact={compact}
        />
      }
      closeOnSelect
    >
      <OptionList
        search={<PopoverSearch value={query} onChange={setQuery} placeholder="Search phases…"/>}
        footer={
          inlineCreate !== false ? inlineCreateFooter : (
            <PopoverFooter>
              <FooterAction icon="plus" label={newPhaseLabel}
                onClick={() => {
                  if (onCreatePhase) {
                    onCreatePhase?.(newPhaseParentId)
                  } else {
                    setInlineCreate({ parentId: newPhaseParentId })
                    setCreateName('')
                  }
                }}/>
              <div style={{ flex: 1 }}/>
              {cur && (
                <FooterAction icon="x" label="Clear" tone="muted"
                  onClick={() => onChange?.(null)}/>
              )}
            </PopoverFooter>
          )
        }
      >
        {!q && (
          <button
            onClick={() => onChange?.(null)}
            style={{
              width: '100%', textAlign: 'left', cursor: 'pointer',
              background: 'transparent', border: 'none',
              padding: '7px 10px', borderRadius: 'var(--r-sm)',
              fontFamily: 'var(--font-sans)', fontSize: 13,
              color: 'var(--text-muted)',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--row-hover)' }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent' }}
          >
            No phase
          </button>
        )}
        {filtered.map(p => (
          <button
            key={p.id}
            onClick={() => onChange?.(p.id)}
            style={{
              width: '100%', textAlign: 'left', cursor: 'pointer',
              background: 'transparent', border: 'none',
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '7px 10px',
              paddingLeft: 10 + (q ? 0 : p._depth * 14),
              borderRadius: 'var(--r-sm)',
              fontFamily: 'var(--font-sans)', fontSize: 13,
              color: 'var(--text-primary)',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--row-hover)' }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent' }}
          >
            {swatch(p.color)}
            <span style={{
              flex: 1, fontWeight: p.parentId ? 500 : 600,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>{p.name}</span>
            {p.range && (
              <span style={{
                fontFamily: 'var(--font-sans)', fontSize: 11,
                color: 'var(--text-muted)', fontWeight: 500,
              }}>{p.range}</span>
            )}
            {p.id === value && (
              <Icon name="check" size={12} sw={2}
                style={{ color: 'var(--brand-gold)', flexShrink: 0 }}/>
            )}
          </button>
        ))}
      </OptionList>
    </Popover>
  )
}

// ─── ParentTaskPicker ─────────────────────────────────────────────────

export interface ParentTaskPickerProps {
  value?: string | null
  currentName?: string | null
  onChange?: (v: string | null) => void
  canEdit: boolean
  options?: { id: string; name: string; status?: string; projectId?: string | number | null }[]
  /** Current task's project — when set, filters out parent-task candidates from other projects (PEEK-SCOPING-01). */
  projectId?: string | null
  compact?: boolean
}

export function ParentTaskPicker({ value = null, currentName, onChange, canEdit, options = [], projectId, compact = false }: ParentTaskPickerProps) {
  const [query, setQuery] = React.useState('')
  const scopedOptions = React.useMemo(() => (
    projectId ? options.filter(t => t.projectId == null || String(t.projectId) === String(projectId)) : options
  ), [options, projectId])
  const cur = scopedOptions.find(t => t.id === value)
  const statusDot = (status?: string) =>
    status ? `var(--status-${status.replace(/_/g, '-')}-dot)` : undefined

  if (!canEdit) {
    if (cur) return <PickerStatic dot={statusDot(cur.status)} label={cur.name} compact={compact}/>
    if (currentName) return <PickerStatic label={currentName} compact={compact}/>
    return <PickerStatic placeholder label="Top-level" compact={compact}/>
  }

  return (
    <Popover
      side="bottom" align="start" width={300}
      trigger={
        <PickerTrigger
          dot={cur ? statusDot(cur.status) : undefined}
          label={cur?.name ?? currentName ?? 'No parent (top-level task)'}
          placeholder={!cur && !currentName}
          compact={compact}
        />
      }
      closeOnSelect
    >
      <OptionList
        search={<PopoverSearch value={query} onChange={setQuery} placeholder="Search tasks…"/>}
      >
        <OptionRow label="No parent · top-level task" checked={!cur}
          onClick={() => onChange?.(null)}/>
        {scopedOptions.length > 0 && (
          <div style={{
            padding: '6px 10px 4px',
            fontFamily: 'var(--font-sans)', fontSize: 10.5, fontWeight: 700,
            letterSpacing: 0.6, textTransform: 'uppercase',
            color: 'var(--text-muted)',
          }}>Same phase</div>
        )}
        {scopedOptions
          .filter(t => !query || t.name.toLowerCase().includes(query.toLowerCase()))
          .map(t => (
            <OptionRow key={t.id}
              dot={statusDot(t.status)}
              label={t.name}
              checked={t.id === value}
              onClick={() => onChange?.(t.id)}/>
          ))}
      </OptionList>
    </Popover>
  )
}

// ─── EquipmentPicker ──────────────────────────────────────────────────

export interface EquipmentPickerProps {
  value?: string[]
  onChange?: (v: string[]) => void
  canEdit: boolean
  options?: EquipmentOption[]
  compact?: boolean
}

export function EquipmentPicker({ value = [], onChange, canEdit, options = [], compact = false }: EquipmentPickerProps) {
  const [query, setQuery] = React.useState('')
  const selected = options.filter(e => value.includes(e.id))

  if (!canEdit) {
    if (selected.length === 0) return <PickerStatic placeholder label="No equipment" compact={compact}/>
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: compact ? '3px 6px' : '6px 0' }}>
        {selected.map(e => (
          <div key={e.id} style={{
            display: 'flex', alignItems: 'center', gap: 8,
            fontFamily: 'var(--font-sans)', fontSize: 13, fontWeight: 500,
          }}>
            <Icon name="briefcase" size={12} sw={1.7} style={{ color: 'var(--text-muted)' }}/>
            {e.name}
            {e.hint && (
              <span style={{ color: 'var(--text-muted)', fontSize: 11.5 }}>
                · {e.hint}
              </span>
            )}
          </div>
        ))}
      </div>
    )
  }

  return (
    <Popover
      side="bottom" align="start" width={300}
      trigger={
        <div style={compact ? {
          width: '100%',
          display: 'flex', alignItems: 'center', gap: 7,
          padding: '0 6px', minHeight: 30,
          background: 'transparent', border: 'none',
          borderRadius: 6, cursor: 'text',
        } : {
          width: '100%',
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '8px 12px', minHeight: 40,
          background: 'var(--bg-elevated)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--r-sm)', cursor: 'text',
        }}>
          <Icon name="search" size={13} sw={2} style={{ color: 'var(--text-muted)', flexShrink: 0 }}/>
          <span style={{
            flex: 1, fontFamily: 'var(--font-sans)', fontSize: 13,
            color: selected.length ? 'var(--text-primary)' : 'var(--text-dim)',
          }}>
            {selected.length === 0
              ? 'Search equipment…'
              : `${selected.length} item${selected.length > 1 ? 's' : ''} attached`}
          </span>
        </div>
      }
    >
      <OptionList
        search={<PopoverSearch value={query} onChange={setQuery} placeholder="Search equipment…"/>}
      >
        {options
          .filter(e => !query || e.name.toLowerCase().includes(query.toLowerCase()))
          .map(e => {
            const checked = value.includes(e.id)
            return (
              <OptionRow key={e.id}
                leading={<Icon name="briefcase" size={13} sw={1.7}
                  style={{ color: 'var(--text-muted)' }}/>}
                label={e.name} hint={e.hint} checked={checked}
                onClick={(ev) => {
                  ev?.stopPropagation?.()
                  onChange?.(checked
                    ? value.filter(v => v !== e.id)
                    : [...value, e.id])
                }}/>
            )
          })}
      </OptionList>
    </Popover>
  )
}

// ─── Repeat / Goal / Billable ─────────────────────────────────────────

export interface RepeatPickerProps {
  value?: string
  onChange?: (v: string) => void
  canEdit: boolean
  compact?: boolean
}

export function RepeatPicker({ value = 'none', onChange, canEdit, compact = false }: RepeatPickerProps) {
  const cur = REPEATS.find(r => r.id === value) ?? REPEATS[0]
  if (!canEdit) return <PickerStatic label={cur.label} placeholder={cur.id === 'none'} compact={compact}/>
  return (
    <Popover
      side="bottom" align="start" width={240}
      trigger={<PickerTrigger label={cur.label} placeholder={cur.id === 'none'} compact={compact}/>}
      closeOnSelect
    >
      <OptionList>
        {REPEATS.map(r => (
          <OptionRow key={r.id} label={r.label} hint={r.hint}
            checked={r.id === value} onClick={() => onChange?.(r.id)}/>
        ))}
      </OptionList>
    </Popover>
  )
}

export interface GoalPickerProps {
  value?: string
  onChange?: (v: string) => void
  canEdit: boolean
  goals?: GoalOption[]
  compact?: boolean
}

export function GoalPicker({ value = 'none', onChange, canEdit, goals = [], compact = false }: GoalPickerProps) {
  const items = [{ id: 'none', label: 'None' }, ...goals.map(g => ({ id: g.id, label: g.name }))]
  const cur = items.find(g => g.id === value) ?? items[0]

  if (!canEdit) return <PickerStatic label={cur.label} placeholder={cur.id === 'none'} compact={compact}/>
  return (
    <Popover
      side="bottom" align="start" width={240}
      trigger={<PickerTrigger label={cur.label} placeholder={cur.id === 'none'} compact={compact}/>}
      closeOnSelect
    >
      <OptionList footer={
        <PopoverFooter>
          <FooterAction icon="plus" label="New goal"/>
        </PopoverFooter>
      }>
        {items.map(g => (
          <OptionRow key={g.id} label={g.label}
            checked={g.id === value} onClick={() => onChange?.(g.id)}/>
        ))}
      </OptionList>
    </Popover>
  )
}

export interface BillablePickerProps {
  value?: string
  onChange?: (v: string) => void
  canEdit: boolean
  compact?: boolean
}

export function BillablePicker({ value = 'billable', onChange, canEdit, compact = false }: BillablePickerProps) {
  const cur = BILLABLE.find(b => b.id === value) ?? BILLABLE[0]
  if (!canEdit) return <PickerStatic label={cur.label} compact={compact}/>
  return (
    <Popover
      side="bottom" align="start" width={240}
      trigger={<PickerTrigger label={cur.label} compact={compact}/>}
      closeOnSelect
    >
      <OptionList>
        {BILLABLE.map(b => (
          <OptionRow key={b.id} label={b.label} hint={b.hint}
            checked={b.id === value} onClick={() => onChange?.(b.id)}/>
        ))}
      </OptionList>
    </Popover>
  )
}

// ─── DatePicker + DateTimePicker ──────────────────────────────────────

export interface PeekDatePickerProps {
  value?: string | null
  onChange?: (v: string | null) => void
  canEdit: boolean
  placeholder?: string
  label?: string
  compact?: boolean
}

export function PeekDatePicker({ value, onChange, canEdit, placeholder = 'Pick a date', compact = false }: PeekDatePickerProps) {
  const parsed = parseDateValue(value)
  const initial = parsed ?? new Date()
  const [year, setYear]   = React.useState(initial.getFullYear())
  const [month, setMonth] = React.useState(initial.getMonth())

  React.useEffect(() => {
    const p = parseDateValue(value)
    if (p) { setYear(p.getFullYear()); setMonth(p.getMonth()) }
  }, [value])

  const selISO = parsed ? fmtDate(parsed) : null
  const display = parsed ? fmtDisplay(parsed, false) : null

  if (!canEdit) {
    return <PickerStatic label={display ?? placeholder} placeholder={!display} compact={compact}/>
  }

  const commit = (d: Date) => onChange?.(fmtDate(d))

  return (
    <Popover
      side="bottom" align="start" width={280}
      trigger={<PickerTrigger label={display ?? placeholder} placeholder={!display} compact={compact}/>}
    >
      <div style={{ width: 280 }}>
        <CalendarHeader
          year={year} month={month}
          onPrev={() => {
            if (month === 0) { setYear(year - 1); setMonth(11) }
            else setMonth(month - 1)
          }}
          onNext={() => {
            if (month === 11) { setYear(year + 1); setMonth(0) }
            else setMonth(month + 1)
          }}
        />
        <CalendarBody
          year={year} month={month} selectedISO={selISO}
          onSelect={(day) => commit(new Date(year, month, day))}
        />
        <PopoverFooter>
          <FooterAction icon="x" label="Clear" tone="muted"
            onClick={() => onChange?.(null)}/>
          <div style={{ flex: 1 }}/>
          <FooterAction icon="check" label="Today"
            onClick={() => {
              const t = new Date()
              setYear(t.getFullYear()); setMonth(t.getMonth())
              commit(t)
            }}/>
        </PopoverFooter>
      </div>
    </Popover>
  )
}

export interface DateTimePickerProps {
  value?: string | null
  onChange?: (v: string | null) => void
  canEdit: boolean
  placeholder?: string
  compact?: boolean
}

export function DateTimePicker({ value, onChange, canEdit, placeholder = 'Pick date & time', compact = false }: DateTimePickerProps) {
  const parsed = parseDateValue(value)
  const initial = parsed ?? new Date()
  const [year, setYear]   = React.useState(initial.getFullYear())
  const [month, setMonth] = React.useState(initial.getMonth())
  const [hour, setHour]     = React.useState(parsed ? parsed.getHours() : 9)
  const [minute, setMinute] = React.useState(parsed ? parsed.getMinutes() : 0)

  React.useEffect(() => {
    const p = parseDateValue(value)
    if (p) {
      setYear(p.getFullYear()); setMonth(p.getMonth())
      setHour(p.getHours()); setMinute(p.getMinutes())
    }
  }, [value])

  const selISO = parsed ? fmtDate(parsed) : null
  const display = parsed ? fmtDisplay(parsed, true) : null

  if (!canEdit) {
    return <PickerStatic label={display ?? placeholder} placeholder={!display} compact={compact}/>
  }

  const commit = (d: Date, h: number, m: number) => {
    const out = new Date(d)
    out.setHours(h); out.setMinutes(m)
    onChange?.(out.toISOString())
  }

  return (
    <Popover
      side="bottom" align="start" width={280}
      trigger={<PickerTrigger label={display ?? placeholder} placeholder={!display} compact={compact}/>}
    >
      <div style={{ width: 280 }}>
        <CalendarHeader
          year={year} month={month}
          onPrev={() => {
            if (month === 0) { setYear(year - 1); setMonth(11) }
            else setMonth(month - 1)
          }}
          onNext={() => {
            if (month === 11) { setYear(year + 1); setMonth(0) }
            else setMonth(month + 1)
          }}
        />
        <CalendarBody
          year={year} month={month} selectedISO={selISO}
          onSelect={(day) => commit(new Date(year, month, day), hour, minute)}
        />
        <div style={{
          padding: '8px 12px', borderTop: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <span style={{
            fontFamily: 'var(--font-mono)', fontSize: 10.5, fontWeight: 700,
            letterSpacing: 0.6, textTransform: 'uppercase', color: 'var(--text-muted)',
          }}>Time</span>
          <select
            value={hour}
            onChange={(e) => {
              const h = Number(e.target.value); setHour(h)
              if (parsed) commit(new Date(year, month, parsed.getDate()), h, minute)
            }}
            style={{
              background: 'var(--bg-elevated)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--r-sm)',
              padding: '4px 8px',
              fontFamily: 'var(--font-sans)', fontSize: 12.5,
              color: 'var(--text-primary)',
            }}
          >
            {Array.from({ length: 24 }, (_, i) => (
              <option key={i} value={i}>
                {((i + 11) % 12 + 1).toString().padStart(2, '0')} {i < 12 ? 'AM' : 'PM'}
              </option>
            ))}
          </select>
          <select
            value={minute}
            onChange={(e) => {
              const m = Number(e.target.value); setMinute(m)
              if (parsed) commit(new Date(year, month, parsed.getDate()), hour, m)
            }}
            style={{
              background: 'var(--bg-elevated)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--r-sm)',
              padding: '4px 8px',
              fontFamily: 'var(--font-sans)', fontSize: 12.5,
              color: 'var(--text-primary)',
            }}
          >
            {[0, 15, 30, 45].map(m => (
              <option key={m} value={m}>:{m.toString().padStart(2, '0')}</option>
            ))}
          </select>
        </div>
        <PopoverFooter>
          <FooterAction icon="x" label="Clear" tone="muted"
            onClick={() => onChange?.(null)}/>
          <div style={{ flex: 1 }}/>
          <FooterAction icon="check" label="Now"
            onClick={() => {
              const t = new Date()
              setYear(t.getFullYear()); setMonth(t.getMonth())
              setHour(t.getHours()); setMinute(t.getMinutes())
              commit(t, t.getHours(), t.getMinutes())
            }}/>
        </PopoverFooter>
      </div>
    </Popover>
  )
}

// ─── DurationInput ────────────────────────────────────────────────────

/** Parses "2d", "4h", "1d 4h", "1.5d" → returns the string as-is after
 *  basic validation. The caller is responsible for storing the canonical form. */
function parseDuration(raw: string): string | null {
  const s = raw.trim()
  if (!s) return null
  // Allow strings like "2d", "4h", "1d 4h", "1.5h", "30m"
  if (/^(\d+(\.\d+)?[dhm]\s*)+$/i.test(s)) return s
  return null
}

export interface DurationInputProps {
  value?: string
  onChange?: (v: string) => void
  canEdit: boolean
  compact?: boolean
}

export function DurationInput({ value = '', onChange, canEdit, compact = false }: DurationInputProps) {
  const [draft, setDraft] = React.useState(value)
  const [invalid, setInvalid] = React.useState(false)

  React.useEffect(() => { setDraft(value) }, [value])

  if (!canEdit) {
    return <TextStatic value={value || null} placeholder="—" mono/>
  }

  const commit = () => {
    const parsed = parseDuration(draft)
    if (draft === '') {
      setInvalid(false)
      onChange?.('')
    } else if (parsed) {
      setInvalid(false)
      onChange?.(parsed)
    } else {
      setInvalid(true)
    }
  }

  return (
    <div style={compact ? {
      display: 'flex', alignItems: 'center', gap: 7,
      padding: '0 6px', minHeight: 30,
      background: 'transparent',
      border: invalid ? '1px solid var(--st-error)' : 'none',
      borderRadius: 6,
    } : {
      display: 'flex', alignItems: 'center', gap: 8,
      padding: '6px 12px', minHeight: 40,
      background: 'var(--bg-elevated)',
      border: `1px solid ${invalid ? 'var(--st-error)' : 'var(--border)'}`,
      borderRadius: 'var(--r-sm)',
      transition: 'border-color var(--dur-fast) var(--ease-out)',
    }}>
      {!compact && <Icon name="clock" size={13} sw={1.7} style={{ color: 'var(--text-muted)', flexShrink: 0 }}/>}
      <input
        value={draft}
        onChange={(e) => { setDraft(e.target.value); setInvalid(false) }}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.currentTarget.blur() } }}
        placeholder="e.g. 2d, 4h, 1d 4h"
        style={{
          flex: 1, background: 'transparent', border: 'none', outline: 'none',
          fontFamily: 'var(--font-mono)', fontSize: 13,
          color: invalid ? 'var(--st-error)' : 'var(--text-primary)',
        }}
      />
    </div>
  )
}
